// Alarm ingest admin (WP 0.6 screen): ingest addresses, parser templates, inbound messages,
// and a test-parse harness. Admin only (App gates the route).

import { useState, type FormEvent } from 'react';
import { api, downloadAuthed, errorText, type IngestAddress, type InboundMessage, type ParserTemplate, type SiteSummary, type TestParseResult } from '../api.ts';
import { Icon } from '../components/Icon.tsx';
import { Card, Empty, ErrorBox, InlineError, JsonBlock, Loading, Pill, Seg, StatusPill, shortId } from '../components/ui.tsx';
import { useAction, useLoad, type Load } from '../hooks.ts';
import { href } from '../router.ts';
import { useSession } from '../session.tsx';
import { fmtBytes, fmtLocal, relAge } from '../time.ts';

/* ---------- addresses ---------- */

const hoursUnset = (s: SiteSummary) => !s.contractedHours || s.contractedHours.mode === 'none';

function CreateAddress({ templates, onCreated }: { templates: ParserTemplate[]; onCreated: () => void }) {
  const { config } = useSession();
  const sites = useLoad((signal) => api.sites(undefined, signal), []);
  const [siteId, setSiteId] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [localPart, setLocalPart] = useState('');
  const [made, setMade] = useState<IngestAddress | null>(null);
  const create = useAction(async () => {
    const a = await api.createIngestAddress({
      siteId,
      ...(templateId ? { parserTemplateId: templateId } : {}),
      ...(localPart.trim() ? { localPart: localPart.trim() } : {}),
    });
    setMade(a);
    setLocalPart('');
    onCreated();
    return a;
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (siteId) void create.run();
  };
  const siteList = (sites.data ?? []).slice().sort((a, b) => a.companyName.localeCompare(b.companyName) || a.name.localeCompare(b.name));
  // The core refuses an address for a site without contracted hours (every alarm would auto-close); list those apart.
  const withHours = siteList.filter((s) => !hoursUnset(s));
  const withoutHours = siteList.filter(hoursUnset);
  const chosen = siteList.find((s) => s.id === siteId);
  const blocked = chosen && hoursUnset(chosen) ? chosen : null;
  const activeTemplates = templates.filter((t) => t.status === 'active');

  return (
    <form className="formrow" onSubmit={submit} aria-label="Create ingest address">
      <label className="field">
        <span className="lbl">Site</span>
        <select value={siteId} onChange={(e) => setSiteId(e.target.value)} required disabled={!sites.data}>
          <option value="">{sites.data ? 'Choose a site…' : sites.error ? 'Sites unavailable' : 'Loading sites…'}</option>
          {withHours.map((s) => (
            <option key={s.id} value={s.id}>
              {s.companyName} · {s.name}
            </option>
          ))}
          {withoutHours.length > 0 && (
            <optgroup label="No contracted hours in the ERP (alarms would auto-close)">
              {withoutHours.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.companyName} · {s.name} · no hours
                </option>
              ))}
            </optgroup>
          )}
        </select>
      </label>
      <label className="field">
        <span className="lbl">Parser template (optional)</span>
        <select value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
          <option value="">Auto-detect by match rules</option>
          {activeTemplates.map((t) => (
            <option key={t.id} value={t.id}>
              {t.vendor} v{t.version}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        <span className="lbl">Local part (optional)</span>
        <span className="addr-in">
          <input value={localPart} onChange={(e) => setLocalPart(e.target.value)} placeholder="generated" spellCheck={false} />
          <span className="mono dim">@{config.alarmDomain || 'alarm domain'}</span>
        </span>
      </label>
      <div className="field end">
        <button type="submit" className="btn pri" disabled={!siteId || !!blocked || create.busy}>
          <Icon name="mail" />
          {create.busy ? 'Creating…' : 'Create address'}
        </button>
      </div>
      {blocked && (
        <span className="inline-err" role="status">
          {blocked.name} has no contracted hours in the ERP ({blocked.contractedHours ? 'mode none' : 'not set'}): alarms would auto-close. Enter its hours in the ERP first.
        </span>
      )}
      {sites.error ? <span className="inline-err">Sites: {errorText(sites.error)}</span> : null}
      <InlineError error={create.error} />
      {made && !create.error && (
        <span className="ok-t" role="status">
          Created <span className="mono">{made.address}</span>
        </span>
      )}
    </form>
  );
}

function AddressRow({ a, templates, onChanged }: { a: IngestAddress; templates: ParserTemplate[]; onChanged: (a: IngestAddress) => void }) {
  const patch = useAction(async (body: { active?: boolean; parserTemplateId?: string | null }) => {
    const r = await api.patchIngestAddress(a.id, body);
    onChanged(r);
    return r;
  });
  const choices = templates.filter((t) => t.status === 'active' || t.id === a.parserTemplateId);
  return (
    <tr className={a.active ? '' : 'inactive'}>
      <td className="mono">{a.address}</td>
      <td>
        {a.siteId ? <a href={href.site(a.siteId, 'ingest')}>{a.siteName ?? a.siteId}</a> : '—'}
        {a.warning && (
          <div className="note warn-t">
            <Icon name="alert" />
            {a.warning}
          </div>
        )}
      </td>
      <td>
        <select
          value={a.parserTemplateId ?? ''}
          disabled={patch.busy}
          onChange={(e) => void patch.run({ parserTemplateId: e.target.value || null })}
          aria-label={`Parser template for ${a.address}`}
          className="compact"
        >
          <option value="">Auto-detect{a.parserVendor && !a.parserTemplateId ? ` (${a.parserVendor})` : ''}</option>
          {choices.map((t) => (
            <option key={t.id} value={t.id}>
              {t.vendor} v{t.version}
              {t.status !== 'active' ? ` (${t.status})` : ''}
            </option>
          ))}
        </select>
      </td>
      <td>
        <label className="toggle">
          <input type="checkbox" role="switch" checked={a.active} disabled={patch.busy} onChange={(e) => void patch.run({ active: e.target.checked })} aria-label={`Active: ${a.address}`} />
          <span className="tog" aria-hidden="true" />
          <span className="small">{a.active ? 'Active' : 'Off'}</span>
        </label>
        <InlineError error={patch.error} />
      </td>
      <td className="mono nowrap" title={fmtLocal(a.lastReceivedAt)}>
        {a.lastReceivedAt ? relAge(a.lastReceivedAt) : 'Never'}
      </td>
      <td className="mono nowrap" title={fmtLocal(a.testReceivedAt)}>
        {a.testReceivedAt ? relAge(a.testReceivedAt) : 'Never'}
      </td>
    </tr>
  );
}

function Addresses({ templates, tload }: { templates: ParserTemplate[]; tload: Load<ParserTemplate[]> }) {
  const load = useLoad((signal) => api.ingestAddresses(signal), []);
  const rows = load.data;
  const [q, setQ] = useState('');
  const needle = q.trim().toLowerCase();
  const shown = (rows ?? []).filter((a) => !needle || `${a.address} ${a.siteName ?? ''}`.toLowerCase().includes(needle));
  return (
    <Card
      title={<><Icon name="mail" />Ingest addresses{rows ? <span className="n">{rows.length}</span> : null}</>}
      right={<input type="search" className="compact" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter" aria-label="Filter addresses" />}
    >
      <CreateAddress templates={templates} onCreated={load.reload} />
      {tload.error ? <span className="note warn-t">Templates unavailable: {errorText(tload.error)}</span> : null}
      {load.error && !rows ? (
        <ErrorBox error={load.error} onRetry={load.reload} title="Could not load ingest addresses" />
      ) : !rows ? (
        <Loading />
      ) : shown.length === 0 ? (
        <Empty>{rows.length ? 'No addresses match the filter.' : 'No ingest addresses yet. Create one above for a site.'}</Empty>
      ) : (
        <div className="tw">
          <table>
            <thead>
              <tr>
                <th scope="col">Address</th>
                <th scope="col">Site</th>
                <th scope="col">Template</th>
                <th scope="col">Active</th>
                <th scope="col">Last alarm</th>
                <th scope="col">Last test</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((a) => (
                <AddressRow key={a.id} a={a} templates={templates} onChanged={(n) => load.setData((cur) => cur?.map((x) => (x.id === n.id ? n : x)))} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

/* ---------- templates ---------- */

function TemplateRow({ t, onActivated }: { t: ParserTemplate; onActivated: () => void }) {
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const act = useAction(async () => {
    await api.activateTemplate(t.id);
    setConfirm(false);
    onActivated();
  });
  return (
    <>
      <tr>
        <td className="strong">{t.vendor}</td>
        <td className="mono">v{t.version}</td>
        <td>{t.kind}</td>
        <td>
          <StatusPill status={t.status} />
        </td>
        <td className="mono num">{t.priority}</td>
        <td className="mono nowrap">{fmtLocal(t.createdAt)}</td>
        <td className="nowrap">
          <span className="btns">
            <button type="button" className="btn sm" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
              {open ? 'Hide spec' : 'View spec'}
            </button>
            {t.status === 'draft' && (
              <button type="button" className="btn sm" onClick={() => setConfirm(true)} disabled={confirm}>
                Activate
              </button>
            )}
          </span>
        </td>
      </tr>
      {(open || confirm) && (
        <tr className="subrow">
          <td colSpan={7}>
            {confirm && (
              <div className="confirmbar">
                <span>
                  Activate {t.vendor} v{t.version}? Older active versions of {t.vendor} are retired.
                </span>
                <button type="button" className="btn pri" disabled={act.busy} onClick={() => void act.run()}>
                  {act.busy ? 'Activating…' : 'Yes, activate'}
                </button>
                <button type="button" className="btn" onClick={() => setConfirm(false)}>
                  Back
                </button>
                <InlineError error={act.error} />
              </div>
            )}
            {open && (
              <div className="cols2">
                <div className="sec">
                  <span className="lbl">Match</span>
                  <JsonBlock value={t.match} maxHeight={320} />
                </div>
                <div className="sec">
                  <span className="lbl">Spec</span>
                  <JsonBlock value={t.spec} maxHeight={320} />
                </div>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function Templates({ load }: { load: Load<ParserTemplate[]> }) {
  const rows = load.data;
  const sorted = (rows ?? []).slice().sort((a, b) => a.vendor.localeCompare(b.vendor) || b.version - a.version);
  return (
    <Card title={<><Icon name="file" />Parser templates</>} right={<button type="button" className="btn sm" onClick={load.reload}><Icon name="sync" />Refresh</button>}>
      {load.error && !rows ? (
        <ErrorBox error={load.error} onRetry={load.reload} title="Could not load templates" />
      ) : !rows ? (
        <Loading />
      ) : sorted.length === 0 ? (
        <Empty>No parser templates yet.</Empty>
      ) : (
        <div className="tw">
          <table>
            <thead>
              <tr>
                <th scope="col">Vendor</th>
                <th scope="col">Version</th>
                <th scope="col">Kind</th>
                <th scope="col">Status</th>
                <th scope="col" className="num">Priority</th>
                <th scope="col">Created</th>
                <th scope="col">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((t) => (
                <TemplateRow key={t.id} t={t} onActivated={load.reload} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

/* ---------- messages + harness ---------- */

type MsgFilter = '' | 'quarantined' | 'unparsed' | 'pending' | 'parsed';
const FILTERS: readonly { value: MsgFilter; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'quarantined', label: 'Quarantined' },
  { value: 'unparsed', label: 'Unparsed' },
  { value: 'pending', label: 'Pending' },
  { value: 'parsed', label: 'Parsed' },
];

function Harness({ messages, templates, messageId, setMessageId }: { messages: InboundMessage[]; templates: ParserTemplate[]; messageId: string; setMessageId: (id: string) => void }) {
  const [templateId, setTemplateId] = useState('');
  const [result, setResult] = useState<TestParseResult | null>(null);
  const run = useAction(async () => {
    setResult(null);
    const r = await api.testParse({ inboundMessageId: messageId, templateId });
    setResult(r);
    return r;
  });
  const sortedT = templates.slice().sort((a, b) => a.vendor.localeCompare(b.vendor) || b.version - a.version);
  return (
    <Card title={<><Icon name="play" />Test-parse harness</>}>
      <div className="note">Runs a template against a stored message. Nothing is stored.</div>
      <form
        className="formrow"
        onSubmit={(e) => {
          e.preventDefault();
          if (messageId && templateId) void run.run();
        }}
      >
        <label className="field">
          <span className="lbl">Message</span>
          <select value={messageId} onChange={(e) => setMessageId(e.target.value)} required>
            <option value="">Choose a message…</option>
            {messageId && !messages.some((m) => m.id === messageId) && <option value={messageId}>{messageId}</option>}
            {messages.map((m) => (
              <option key={m.id} value={m.id}>
                {fmtLocal(m.receivedAt)} · {m.rcptTo} · {m.parseStatus}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="lbl">Template</span>
          <select value={templateId} onChange={(e) => setTemplateId(e.target.value)} required>
            <option value="">Choose a template…</option>
            {sortedT.map((t) => (
              <option key={t.id} value={t.id}>
                {t.vendor} v{t.version} ({t.status})
              </option>
            ))}
          </select>
        </label>
        <div className="field end">
          <button type="submit" className="btn pri" disabled={!messageId || !templateId || run.busy}>
            <Icon name="play" />
            {run.busy ? 'Parsing…' : 'Run test parse'}
          </button>
        </div>
      </form>
      <InlineError error={run.error} />
      {result && (
        <div className="sec">
          <div className="row-wrap">
            <Pill tone={result.ok ? 'ok' : 'bad'}>{result.ok ? 'ok' : 'failed'}</Pill>
            <Pill tone={result.matched ? 'ok' : 'warn'}>{result.matched ? 'match rules matched' : 'match rules did not match'}</Pill>
            {result.alarm && (
              <span className="small">
                → <b>{result.alarm.analytic ?? 'no analytic'}</b> · site <span className="mono">{result.alarm.siteId ?? '—'}</span> · camera{' '}
                <span className="mono">{result.alarm.cameraId ?? '—'}</span> · {result.alarm.attachments.length} attachment
                {result.alarm.attachments.length === 1 ? '' : 's'}
                {result.alarm.flags.length ? ` · flags: ${result.alarm.flags.join(', ')}` : ''}
              </span>
            )}
          </div>
          {result.error && <div className="inline-err">{result.error}</div>}
          <JsonBlock value={result} maxHeight={420} />
        </div>
      )}
    </Card>
  );
}

function Messages({ templates }: { templates: ParserTemplate[] }) {
  const [status, setStatus] = useState<MsgFilter>('');
  const load = useLoad((signal) => api.messages({ status: status || undefined, limit: 100 }, signal), [status]);
  const rows = load.data;
  const [harnessMsg, setHarnessMsg] = useState('');
  const dl = useAction(downloadAuthed);
  const tById = new Map(templates.map((t) => [t.id, t]));
  return (
    <>
      <Card
        title={<><Icon name="mail" />Recent inbound messages</>}
        right={
          <>
            <Seg label="Parse status" value={status} options={FILTERS} onChange={setStatus} />
            <button type="button" className="btn sm" onClick={load.reload} disabled={load.loading}>
              <Icon name="sync" />
              Refresh
            </button>
          </>
        }
      >
        <InlineError error={dl.error} />
        {load.error && !rows ? (
          <ErrorBox error={load.error} onRetry={load.reload} title="Could not load messages" />
        ) : !rows ? (
          <Loading />
        ) : rows.length === 0 ? (
          <Empty>No {status || 'inbound'} messages.</Empty>
        ) : (
          <div className="tw">
            <table>
              <thead>
                <tr>
                  <th scope="col">Received</th>
                  <th scope="col">To</th>
                  <th scope="col">From</th>
                  <th scope="col" className="num">Size</th>
                  <th scope="col">Status</th>
                  <th scope="col">Template</th>
                  <th scope="col">Alarms</th>
                  <th scope="col">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((m) => {
                  const t = m.parserTemplateId ? tById.get(m.parserTemplateId) : undefined;
                  return (
                    <tr key={m.id} className={harnessMsg === m.id ? 'sel' : ''}>
                      <td className="mono nowrap" title={m.receivedAt}>
                        {fmtLocal(m.receivedAt)}
                      </td>
                      <td className="mono ell">{m.rcptTo}</td>
                      <td className="mono ell">{m.smtpFrom}</td>
                      <td className="mono num">{fmtBytes(m.size)}</td>
                      <td>
                        <StatusPill status={m.parseStatus} />
                        {m.rateLimited && <Pill tone="warn">rate limited</Pill>}
                        {m.parseError && <div className="note warn-t">{m.parseError}</div>}
                      </td>
                      <td className="mono small">{t ? `${t.vendor} v${t.version}` : m.parserTemplateId ? shortId(m.parserTemplateId) : '—'}</td>
                      <td className="mono small">
                        {m.alarmIds.length ? (
                          <a href={href.alarms()} title={m.alarmIds.join(', ')}>
                            {m.alarmIds.length} alarm{m.alarmIds.length === 1 ? '' : 's'}
                          </a>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="nowrap">
                        <span className="btns">
                          <button type="button" className="btn sm" onClick={() => void dl.run(api.rawMessageUrl(m.id), `${m.id}.eml`)} disabled={dl.busy}>
                            <Icon name="download" />
                            .eml
                          </button>
                          <button type="button" className="btn sm" onClick={() => setHarnessMsg(m.id)}>
                            Test parse
                          </button>
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      <Harness messages={rows ?? []} templates={templates} messageId={harnessMsg} setMessageId={setHarnessMsg} />
    </>
  );
}

/* ---------- screen ---------- */

export function IngestScreen() {
  const templates = useLoad((signal) => api.templates(signal), []);
  const t = templates.data ?? [];
  return (
    <div className="page">
      <div className="page-h">
        <div>
          <h2>Alarm ingest</h2>
          <p>SMTP addresses per site, vendor parser templates, inbound messages, and a parse test harness.</p>
        </div>
      </div>
      <Addresses templates={t} tload={templates} />
      <Templates load={templates} />
      <Messages templates={t} />
    </div>
  );
}
